import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useLocation, useHistory } from 'react-router-dom'
import { useServerContext } from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import { twMerge } from 'tailwind-merge'
import { MIN_AMOUNT_FREE_SHIPPING, MIN_BOXES_FREE_DELIVERY } from '@/constants'
import {
  Button,
  CartDHLInfo,
  CartFooterLinks,
  CartHeader,
  CartPaymentInfo,
  ScrollTo,
  CartTopLabels,
  CartUSP,
  Icons,
  MainWrapper,
  OrderSummary,
  PromoCode,
  Spinner,
  AlertBox
} from 'msp-components'

// Hooks
import {
  getEventItemsFromBasket,
  Region,
  transformRegionToMap,
  useBasket,
  useCookie,
  useEinstein,
  useNavigation,
  useWindowSize,
  useConfig
} from 'msp-integrations'

// Project Components
import JasminMiniCart from '@/components/organisms/JasminMiniCart/JasminMiniCart'
import PageLayout from '@/components/cms/page-layout'
import Seo, { useSeo } from '@/components/seo'
import ContentCardBasket from '@/components/organisms/ContentCardBasket/ContentCardBasket'

const CartNextStepButton = ({
  className,
  isDisabled,
  onClick
}: {
  className: string
  isDisabled: boolean
  onClick?: () => void
}) => {
  const navigate = useNavigation()
  const history = useHistory()
  const { formatMessage } = useIntl()
  const location = useLocation()
  const [handleAlertError, setHandleAlertError] = useState<{
    isError: boolean
    alertMessage: string
  }>({ isError: false, alertMessage: '' })
  const alertBoxRef = useRef<AlertBox>()
  const intl = useIntl()
  const basket = useBasket()
  const { c_remainingBrewsToMinimum } = basket

  useEffect(() => {
    const brewBoxWordSingle = intl.formatMessage({
      id: 'msp.cart.shipment.brewbox.single',
      defaultMessage: 'Tea box'
    })

    const brewBoxWordMultiple = intl.formatMessage({
      id: 'msp.cart.shipment.brewbox.multiple',
      defaultMessage: 'Tea boxes'
    })

    const brewBoxWord =
      c_remainingBrewsToMinimum === 1 ? brewBoxWordSingle : brewBoxWordMultiple

    const mindest = intl.formatMessage({
      id: 'msp.cart.shipment.minimum.order.term',
      defaultMessage: 'Minimum order quantity'
    })

    const searchParams = new URLSearchParams(location.search)
    if (
      searchParams.get('openModal') === 'brewsToMinimum' &&
      c_remainingBrewsToMinimum > 0
    ) {
      const alertMessage = intl.formatMessage(
        {
          id: 'msp.cart.shipment.minimum.order.quantity',
          defaultMessage: 'Add {quantity} {brewBoxWord} to reach {mindest}'
        },
        {
          quantity: c_remainingBrewsToMinimum,
          brewBoxWord: brewBoxWord,
          mindest: mindest
        }
      )

      setHandleAlertError({
        isError: true,
        alertMessage: alertMessage
      })
      searchParams.delete('openModal')
      history.replace({
        search: searchParams.toString()
      })
      alertBoxRef?.current?.showAlert()
    }
  }, [location.search, c_remainingBrewsToMinimum, intl])

  return (
    <div className={className || ''}>
      <AlertBox
        ref={alertBoxRef}
        title='Alert'
        description={handleAlertError.alertMessage}
        closeButtonIcon={<Icons iconName='Close' size='base' />}
        type={handleAlertError?.isError ? 'error' : 'warning'}
        autoCloseTime={4000}
        onClose={() =>
          setHandleAlertError({ isError: false, alertMessage: '' })
        }
      />
      <Button
        type='primary'
        className='mb-3 w-full text-white'
        iconName='ArrowRight'
        iconPosition='right'
        isDisabled={isDisabled}
        onClick={() => (onClick ? onClick() : navigate('/checkout'))}
        data-cy={'goto-checkout-button'}
      >
        {formatMessage({
          id: 'msp.cart.cta.go.to.checkout',
          defaultMessage: 'To checkout'
        })}
      </Button>
      <CartPaymentInfo
        icons={
          <>
            <Icons iconName='PayPal' size='xl' />
            <Icons iconName='VisaCard' size='xl' />
            <Icons iconName='MasterCard' size='xl' />
          </>
        }
      />
    </div>
  )
}

const Cart = ({ page }: any) => {
  const { eventTracking, getCookieAllowedById } = useCookie()
  const basket = useBasket()
  const { formatMessage, formatNumber } = useIntl()
  const location = useLocation()
  const einstein = useEinstein()
  const navigate = useNavigation()
  const { width, breakpoints } = useWindowSize()
  const isMobile = width && width < breakpoints.xl
  const regions = transformRegionToMap(page?.content?.regions)
  const isSalesforceAllowed = getCookieAllowedById('s291') // CONSENTS['s291'] = 'salesforce'
  const {
    c_minAmountFreeShipping,
    productsQuantity,
    c_minBoxesFreeDelivery,
    c_isDisabledButtonCheckout
  } = basket
  const minAmountFreeShipping =
    c_minAmountFreeShipping || MIN_AMOUNT_FREE_SHIPPING
  const minBoxesFreeDelivery = c_minBoxesFreeDelivery || MIN_BOXES_FREE_DELIVERY
  const {
    theme: { font }
  } = useConfig()
  const { seoTitle, seoDescription, seoKeywords } = useSeo({
    page,
    title: { id: 'msp.cart.seo.title', defaultMessage: 'Cart' },
    description: {
      id: 'msp.cart.seo.description',
      defaultMessage: 'This is the Avoury Cart page.'
    }
  })
  const { hasOfflineItemsInCart, itemTotal, currency } = basket

  const promoCodeInfoText = formatMessage({
    id: 'msp.cart.promo.code.info',
    defaultMessage: `You can redeem a gift card with credit in the next step under 'Payment'.`
  })

  const formatPrice = (price?: number) =>
    typeof price !== 'undefined' && currency
      ? formatNumber(price, {
          style: 'currency',
          currency: currency,
          currencyDisplay: 'code'
        })
      : ''

  const [cartStockErrors, setCartStockErrors] = useState<
    { [productId: string]: number | null } | undefined
  >(undefined)
  const [isCheckingStock, setIsCheckingStock] = useState(false)
  const [cartStockAlertMessage, setCartStockAlertMessage] =
    useState<React.ReactNode>('')
  const cartStockAlertRef = useRef<AlertBox>()

  const checkStock = async () => {
    const productItems: any[] = basket?.productItems || []
    const stockErrors: { [productId: string]: number | null } = {}
    const adjustments: Promise<any>[] = []

    // Refetch fresh inventory for all products in parallel
    const freshInventories = await Promise.allSettled(
      productItems.map((item) =>
        basket.getProductDetail(item.productId).then((p: any) => ({
          productId: item.productId,
          inventory: p?.c_inventory
        }))
      )
    )

    const inventoryMap: Record<string, any> = {}
    for (const result of freshInventories) {
      if (result.status === 'fulfilled' && result.value?.inventory) {
        inventoryMap[result.value.productId] = result.value.inventory
      }
    }

    for (const item of productItems) {
      const inventory = inventoryMap[item.productId] || item.c_inventory
      if (!inventory) continue
      const available = Number(
        inventory.ats ?? inventory.stockLevel ?? Infinity
      )
      if (!inventory.orderable || available === 0) {
        stockErrors[item.productId] = null
        adjustments.push(basket.removeItemFromBasket(item.itemId))
      } else if (item.quantity > available) {
        stockErrors[item.productId] = available
        adjustments.push(
          basket.updateItemInBasket(
            {
              productId: item.productId,
              quantity: available,
              price: item.price
            },
            item.itemId
          )
        )
      }
    }

    if (Object.keys(stockErrors).length > 0) {
      setIsCheckingStock(true)
      await Promise.allSettled(adjustments)
      setCartStockErrors(stockErrors)

      const detailLines = productItems
        .filter((item) => stockErrors[item.productId] !== undefined)
        .map((item) => {
          const available = stockErrors[item.productId]
          if (available === null || available === 0) {
            return formatMessage(
              {
                id: 'msp.cart.stock_check.product_removed',
                defaultMessage: '"{productName}" has been removed (sold out).'
              },
              { productName: item.productName }
            )
          }
          return formatMessage(
            {
              id: 'msp.cart.stock_check.product_adjusted',
              defaultMessage:
                '"{productName}" has been adjusted from {requestedQuantity} to {stockLevel}.'
            },
            {
              productName: item.productName,
              requestedQuantity: item.quantity,
              stockLevel: available
            }
          )
        })

      setCartStockAlertMessage(
        <>
          {formatMessage({
            id: 'msp.cart.stock_check.summary',
            defaultMessage:
              'Some items in your cart have been adjusted because the desired quantity is no longer available.'
          })}
          {detailLines.map((line, i) => (
            <span key={i}>
              <br />
              {line}
            </span>
          ))}
        </>
      )
      // @ts-ignore
      cartStockAlertRef?.current?.showAlert()
      setIsCheckingStock(false)
    }

    return Object.keys(stockErrors).length > 0
  }

  const checkStockAndNavigate = async () => {
    const hasErrors = await checkStock()
    if (!hasErrors) {
      navigate('/checkout')
    }
  }

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('cart-stock-errors')
      if (raw) {
        setCartStockErrors(JSON.parse(raw))
        sessionStorage.removeItem('cart-stock-errors')
      }
    } catch (_) {}
  }, [])

  useEffect(() => {
    einstein.sendViewPage(location.pathname)
  }, [isSalesforceAllowed])

  useEffect(() => {
    // Set the default shipping method if none is already selected
    if (
      basket.basketId &&
      basket.shipments.length > 0 &&
      !basket.shipments[0].shippingMethod
    ) {
      const defaultShipping = async () => {
        const shippingMethods = await basket.getShippingMethods()
        basket.setShippingMethod(shippingMethods.defaultShippingMethodId)
      }
      defaultShipping()
    }

    if (basket.basketId) {
      eventTracking('view_cart', {
        ecommerce: {
          items: getEventItemsFromBasket(basket)
        }
      })
      checkStock()
    }
  }, [basket.basketId])

  if (!basket?.basketId) {
    return <Spinner fullScreen />
  }

  if (!basket?.productItems && !basket?.giftCertificateItems) {
    return (
      <div className='page-main-wrapper page-bg-image flex justify-center'>
        <div className='checkout-row w-full md:w-3/4 xl:w-1/2'>
          <JasminMiniCart
            basket={basket}
            inCartPage
            minAmountFreeShipping={minAmountFreeShipping}
            minBoxesFreeDelivery={minBoxesFreeDelivery}
            stockErrors={cartStockErrors}
          />
        </div>
      </div>
    )
  }

  return (
    <div className='page-main-wrapper page-bg-image'>
      <AlertBox
        ref={cartStockAlertRef}
        title={formatMessage({
          id: 'msp.commerce_box.add_to_cart.not_orderable_item.title',
          defaultMessage: 'Item(s) could not be added to the basket'
        })}
        description={cartStockAlertMessage}
        closeButtonIcon={<Icons iconName='Close' size='base' />}
        type='error'
        onClose={() => setCartStockAlertMessage('')}
      />
      <ScrollTo to='top' />
      <Seo
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalPath={'/cart'}
      />
      <MainWrapper>
        <div className='checkout-row hidden md:block'>
          <CartTopLabels />
        </div>
        <div className='checkout-row'>
          <div className='checkout-item checkout-left p-0 md:p-7'>
            <CartHeader productsQuantity={productsQuantity} />
            {isMobile && (
              <>
                <div className='block border-b-4 border-solid border-black bg-white'>
                  <div className='mb-3 px-5 pb-5 md:px-0'>
                    <PromoCode
                      applyPromoCode={basket?.applyPromoCode}
                      bonusDiscountLineItems={
                        basket?.bonusDiscountLineItems || []
                      }
                      infoText={promoCodeInfoText}
                    />
                  </div>
                  {isMobile && <ContentCardBasket isDesktop={!isMobile} />}
                  <Region noWrapper region={regions.get('promotions')} />
                </div>
                <div className='mb-4 mt-0.5 border-t border-solid border-black'></div>
                <div className='px-5 md:px-0'>
                  <div className='flex flex-row items-center justify-between py-5'>
                    <div className='flex flex-col space-y-1'>
                      <p className={font.regular}>
                        {formatMessage({
                          id: 'msp.cart.total.amount',
                          defaultMessage: 'Total amount'
                        })}
                      </p>
                      <div
                        className={`${font.regular} text-grayRegular text-14 leading-16 tracking-0.4`}
                      >
                        {formatMessage({
                          id: 'msp.cart.total.amount.vat',
                          defaultMessage: 'incl. VAT, excl. shipping'
                        })}
                      </div>
                      <div
                        className={twMerge(
                          font.regular,
                          'text-grayRegular',
                          'text-14',
                          'leading-16',
                          'tracking-0.4',
                          'pt-4'
                        )}
                      >
                        {formatMessage(
                          {
                            id: 'msp.cart.shipment.info.2',
                            defaultMessage:
                              'Free packaging and shipping from {quantity} EUR or for {quantityBrews} boxes of tea.'
                          },
                          {
                            quantity: minAmountFreeShipping,
                            quantityBrews: minBoxesFreeDelivery
                          }
                        )}
                      </div>
                    </div>
                    <p className={font.bold}>{`${formatPrice(itemTotal)}`}</p>
                  </div>
                  <div className='py-5'>
                    <Button
                      type='primary'
                      variant={'outline'}
                      className='mb-3 w-full'
                      onClick={() => navigate('/shop')}
                    >
                      {formatMessage({
                        id: 'msp.minicart.cta.go.shop',
                        defaultMessage: 'Continue shopping'
                      })}
                    </Button>
                    <Button
                      type='primary'
                      className='mb-3 w-full'
                      iconName='ArrowRight'
                      iconPosition='right'
                      onClick={checkStockAndNavigate}
                      isDisabled={
                        isCheckingStock ||
                        hasOfflineItemsInCart ||
                        c_isDisabledButtonCheckout
                      }
                    >
                      {formatMessage({
                        id: 'msp.cart.cta.go.to.checkout',
                        defaultMessage: 'To checkout'
                      })}
                    </Button>
                    <CartPaymentInfo
                      icons={
                        <>
                          <Icons iconName='PayPal' size='xl' />
                          <Icons iconName='VisaCard' size='xl' />
                          <Icons iconName='MasterCard' size='xl' />
                        </>
                      }
                    />
                  </div>
                </div>
              </>
            )}
            <JasminMiniCart
              basket={basket}
              inCartPage
              minAmountFreeShipping={minAmountFreeShipping}
              minBoxesFreeDelivery={minBoxesFreeDelivery}
              stockErrors={cartStockErrors}
              goToCartButton={
                <CartNextStepButton
                  isDisabled={
                    isCheckingStock ||
                    hasOfflineItemsInCart ||
                    c_isDisabledButtonCheckout
                  }
                  className={twMerge(
                    'flex w-full flex-col',
                    hasOfflineItemsInCart
                      ? 'pointer-events-none cursor-not-allowed'
                      : ''
                  )}
                  onClick={checkStockAndNavigate}
                />
              }
            />
            <div className='overflow-hidden'>
              <Region noWrapper region={regions.get('recomendations')} />
            </div>
          </div>
          <div className='checkout-item checkout-right'>
            <OrderSummary
              className='mb-14'
              basket={basket}
              showProducts={false}
              showShipment={false}
            />
            <PromoCode
              applyPromoCode={basket?.applyPromoCode}
              bonusDiscountLineItems={basket?.bonusDiscountLineItems || []}
              infoText={promoCodeInfoText}
            />
            <span className='border-gray94 my-4 hidden border-b border-solid xl:block' />
            <CartPaymentInfo
              icons={
                <>
                  <Icons iconName='PayPal' size='xl' />
                  <Icons iconName='VisaCard' size='xl' />
                  <Icons iconName='MasterCard' size='xl' />
                </>
              }
            />
            <CartDHLInfo
              minAmountFreeShipping={minAmountFreeShipping}
              minBoxesFreeDelivery={minBoxesFreeDelivery}
            />
            <Button
              type='primary'
              className='my-8 w-full text-white'
              iconName='ArrowRight'
              iconPosition='right'
              isDisabled={
                isCheckingStock ||
                hasOfflineItemsInCart ||
                c_isDisabledButtonCheckout
              }
              onClick={checkStockAndNavigate}
            >
              {formatMessage({
                id: 'msp.cart.cta.go.to.checkout',
                defaultMessage: 'To checkout'
              })}
            </Button>
            {!isMobile && <ContentCardBasket isDesktop={!isMobile} />}
            <Region noWrapper region={regions.get('promotions')} />
          </div>
        </div>
        <div className='checkout-row hidden md:block'>
          <CartUSP />
        </div>
        <div className='checkout-row'>
          <CartFooterLinks />
        </div>
      </MainWrapper>
    </div>
  )
}

const CartWrapper = (props) => {
  const { req } = useServerContext()
  let paymentError
  if (req) {
    paymentError = req.query?.paymentError
  }

  return (
    <PageLayout {...Object.assign({ ChildPage: Cart, paymentError }, props)} />
  )
}

export default CartWrapper
